import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import dayjs from 'dayjs';
import {
  Activity,
  Building2,
  CalendarDays,
  ChartBar,
  FileBarChart,
  Filter,
  Loader2,
  Package,
  Search,
  TrendingUp,
} from 'lucide-react';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import DatePopoverSingle from '@/components/DatePopoverSingle';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

import {
  GetCompaniesListApi,
  GetCompanyItemsDataSupplyApi,
} from './api/CompanyItems.api';
import type {
  ICompanyItemGroupedSupplyInterface,
  ICompanyItemInterface,
  ICompanyList,
} from './api/CompanyItems.interface';
import ClassifiedItems from './components/ClassifiedItems';
import FilterBox from './components/FilterItems';
import { InitialScreen } from './components/IntialScreen';
import ItemsTable from './components/itemTable/ItemTable';
import { AutoCompleteCompanies } from './components/selectCompanyForm/CompaniesList';

// Form validation schema
const formSchema = z
  .object({
    company: z
      .object({
        ID: z.number(),
        Name: z.string(),
        supplierName: z.string().optional(),
      })
      .nullable()
      .refine((val) => val !== null, {
        message: 'يجب اختيار الشركة',
      }),
    startDate: z.string().min(1, 'تاريخ البداية مطلوب'),
    endDate: z.string().min(1, 'تاريخ النهاية مطلوب'),
  })
  .refine(
    (data) => {
      if (data.startDate && data.endDate) {
        return new Date(data.endDate) >= new Date(data.startDate);
      }
      return true;
    },
    {
      message: 'تاريخ النهاية يجب أن يكون بعد تاريخ البداية',
      path: ['endDate'],
    },
  );

type FormData = z.infer<typeof formSchema>;

function CompanyItemsSupplyReportPage() {
  // Get all companies query to use it in the form dialog
  const allCompanies = useQuery({
    queryFn: () => GetCompaniesListApi(),
    queryKey: ['all-companies'],
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [allData, setAllData] = useState<ICompanyItemInterface[] | null>(null);
  const [groupedData, setGroupedData] = useState<
    ICompanyItemGroupedSupplyInterface[] | null
  >(null);
  const [filterData, setFilterData] = useState<ICompanyItemInterface[] | null>(
    null,
  );
  const [selectedCompany, setSelectedCompany] = useState<ICompanyList | null>(
    null,
  );
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  // Form setup
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      company: null,
      startDate: '',
      endDate: '',
    },
  });

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    setValue,
    watch,
    reset,
  } = form;

  const formValues = watch();

  // API mutation for fetching data
  const getItemsMutation = useMutation({
    mutationFn: GetCompanyItemsDataSupplyApi,
    onSuccess: async (data) => {
      setAllData(data?.data?.transactions);
      setFilterData(data?.data?.transactions);
      setGroupedData(data?.data?.groupItems);
      setLoading(false);
      toast.success('تم جلب البيانات بنجاح', {
        position: 'bottom-center',
      });
      if (data?.data?.count === 0 || !data?.data?.transactions) {
        toast.error('لا توجد حركات لأصناف هذه الشركة خلال هذه المدة', {
          position: 'top-right',
        });
      }
    },
    onError: (err: AxiosError<{ message?: string }>) => {
      setLoading(false);
      toast.error(err?.response?.data?.message || 'حدث خطأ ما', {
        position: 'top-right',
      });
    },
  });

  const onSubmit = async (values: FormData) => {
    setAllData(null);
    setGroupedData(null);
    setFilterData(null);
    setLoading(true);
    if (values?.company?.ID) {
      await getItemsMutation.mutateAsync({
        id: values?.company?.ID,
        startDate: dayjs(values.startDate).format('YYYY-MM-DD'),
        endDate: dayjs(values.endDate).format('YYYY-MM-DD'),
      });
    }
  };

  if (allCompanies?.isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Card className="border-0 shadow-lg">
          <CardContent className="flex flex-col items-center justify-center space-y-4 p-12">
            <div className="relative">
              <div className="absolute inset-0 h-20 w-20 animate-ping rounded-full bg-primary/20" />
              <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-primary/10 to-primary/5">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
              </div>
            </div>
            <div className="space-y-2 text-center">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                جاري تحميل البيانات
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                يرجى الانتظار...
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>تقرير توريد أصناف الشركات</title>
      </Helmet>

      <div className="min-h-screen w-full bg-slate-50/50 p-6 dark:bg-slate-950/50">
        {/* Unified Header with Integrated Form */}
        <Card className="border-0 bg-white p-3 shadow-lg dark:bg-slate-900">
          <CardHeader className="px-8 py-8 pb-0">
            <div className="space-y-6">
              {/* Title Section */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 to-primary/5">
                    <FileBarChart className="h-7 w-7 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                      تقرير توريد أصناف الشركات
                    </CardTitle>
                    <p className="mt-1 flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                      <Activity className="h-3.5 w-3.5" />
                      تحليل شامل لحركة التوريد
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {selectedCompany && (
                    <Badge
                      variant="soft"
                      className="flex h-8 items-center gap-2 px-4"
                    >
                      <Building2 className="h-3.5 w-3.5" />
                      {selectedCompany.Name}
                    </Badge>
                  )}
                  {startDate && endDate && (
                    <Badge
                      variant="outline"
                      className="flex h-8 items-center gap-2 px-4"
                    >
                      <CalendarDays className="h-3.5 w-3.5" />
                      {dayjs(startDate).format('DD/MM/YYYY')} -{' '}
                      {dayjs(endDate).format('DD/MM/YYYY')}
                    </Badge>
                  )}
                  <Badge
                    variant="soft"
                    className={cn(
                      'h-8 px-4',
                      loading
                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400'
                        : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400',
                    )}
                  >
                    <div
                      className={cn(
                        'mr-2 h-2 w-2 rounded-full',
                        loading
                          ? 'animate-pulse bg-amber-500'
                          : 'bg-emerald-500',
                      )}
                    />
                    {loading
                      ? 'جاري التحميل'
                      : allData
                        ? 'البيانات جاهزة'
                        : 'في انتظار البحث'}
                  </Badge>
                </div>
              </div>

              {/* Form Section */}
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="border-t border-slate-200 pt-6 dark:border-slate-700"
              >
                <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
                  {/* Company Selection */}
                  <div className="md:col-span-1">
                    <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                      الشركة <span className="text-red-500">*</span>
                    </label>
                    <AutoCompleteCompanies
                      options={allCompanies?.data?.data?.companies || []}
                      searchPlaceholder="ابحث عن الشركة"
                      onValueChange={(data) => {
                        setValue('company', data);
                        setSelectedCompany(data);
                      }}
                      value={formValues.company}
                    />
                    {errors.company && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.company.message}
                      </p>
                    )}
                  </div>

                  {/* Start Date */}
                  <div className="md:col-span-1">
                    <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                      تاريخ البداية <span className="text-red-500">*</span>
                    </label>
                    <DatePopoverSingle
                      selectFn={(date) => {
                        setValue('startDate', date || '');
                        setStartDate(date ? new Date(date) : null);
                      }}
                      value={formValues?.startDate}
                      placeholder="اختر تاريخ البداية"
                    />
                    {errors.startDate && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.startDate.message}
                      </p>
                    )}
                  </div>

                  {/* End Date */}
                  <div className="md:col-span-1">
                    <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                      تاريخ النهاية <span className="text-red-500">*</span>
                    </label>
                    <DatePopoverSingle
                      selectFn={(date) => {
                        setValue('endDate', date || '');
                        setEndDate(date ? new Date(date) : null);
                      }}
                      value={formValues?.endDate}
                      placeholder="اختر تاريخ النهاية"
                    />
                    {errors.endDate && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors.endDate.message}
                      </p>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-end gap-2 md:col-span-1">
                    <Button
                      type="submit"
                      disabled={isSubmitting || loading}
                      className="h-10 flex-1"
                    >
                      {isSubmitting || loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          جاري البحث
                        </>
                      ) : (
                        <>
                          <Search className="mr-2 h-4 w-4" />
                          بحث
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        reset();
                        setAllData(null);
                        setFilterData(null);
                        setGroupedData(null);
                        setSelectedCompany(null);
                        setStartDate(null);
                        setEndDate(null);
                      }}
                      disabled={isSubmitting || loading}
                      className="h-10 px-4"
                    >
                      مسح
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </CardHeader>
        </Card>

        {/* Main Content Grid */}
        <div className="mt-4 grid h-[calc(100vh-200px)] grid-cols-1 gap-6 lg:grid-cols-[500px_1fr]">
          {allData && (
            <>
              {/* Left Sidebar - Filter Section */}
              <div className="flex h-full flex-col">
                <Card className="flex-1 overflow-hidden border-0 bg-white shadow-lg dark:bg-slate-900">
                  <CardHeader className="border-b border-slate-200 bg-slate-50/50 px-6 py-4 dark:border-slate-700 dark:bg-slate-800/30">
                    <div className="flex items-center gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-primary/20 bg-gradient-to-br from-primary/10 to-primary/5">
                        <Filter className="h-6 w-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-lg font-bold text-slate-900 dark:text-slate-100">
                          فلاتر البحث
                        </CardTitle>
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          {filterData?.length || 0} نتيجة من{' '}
                          {allData?.length || 0} عنصر
                        </p>
                      </div>
                      {filterData &&
                        allData &&
                        filterData.length !== allData.length && (
                          <Badge variant="soft" className="h-8 px-3">
                            مفلتر
                          </Badge>
                        )}
                    </div>
                  </CardHeader>
                  <CardContent className="flex-1 overflow-hidden p-0">
                    <div className="h-full p-6">
                      <FilterBox
                        allData={allData}
                        filterData={filterData}
                        setFilterData={setFilterData}
                        groupedData={groupedData}
                        selectedCompany={selectedCompany}
                        startDate={startDate}
                        endDate={endDate}
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Right Content Area */}
              <div className="flex h-full flex-col space-y-6 overflow-hidden">
                {/* Statistics Section */}
                {filterData && (
                  <div className="flex-shrink-0">
                    <div className="mb-4 flex items-center justify-between">
                      <h2 className="flex items-center gap-3 text-xl font-bold text-slate-900 dark:text-slate-100">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary/10 to-primary/5">
                          <ChartBar className="h-5 w-5 text-primary" />
                        </div>
                        إحصائيات التوريد
                      </h2>
                      <Badge variant="soft" className="h-8 gap-2 px-4">
                        <TrendingUp className="h-4 w-4" />
                        محدث الآن
                      </Badge>
                    </div>
                    <ClassifiedItems items={filterData} />
                  </div>
                )}

                {/* Data Table Section */}
                <Card className="flex-1 overflow-hidden border-0 bg-white shadow-lg dark:bg-slate-900">
                  <CardHeader className="border-b border-slate-200 bg-slate-50/50 px-6 py-4 dark:border-slate-700 dark:bg-slate-800/30">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-primary/20 bg-gradient-to-br from-primary/10 to-primary/5">
                          <Package className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-lg font-bold text-slate-900 dark:text-slate-100">
                            تفاصيل الأصناف
                          </CardTitle>
                          <p className="text-sm text-slate-600 dark:text-slate-400">
                            جدول شامل بجميع البيانات
                          </p>
                        </div>
                      </div>
                      {filterData && (
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="h-8 px-4 text-sm">
                            {filterData.length} صنف
                          </Badge>
                          <Badge variant="soft" className="h-8 px-4 text-sm">
                            جاهز
                          </Badge>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="flex-1 overflow-hidden p-0">
                    <div className="h-full overflow-auto">
                      <ItemsTable items={filterData || undefined} />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}

          {/* Initial Screen - No Data State */}
          {!allData && !loading && (
            <div className="col-span-full flex h-full items-center justify-center">
              <InitialScreen />
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="col-span-full flex h-full items-center justify-center">
              <Card className="border-0 bg-white shadow-xl dark:bg-slate-900">
                <CardContent className="flex flex-col items-center justify-center space-y-8 p-20">
                  <div className="relative">
                    <div className="absolute inset-0 h-28 w-28 animate-pulse rounded-full bg-primary/10" />
                    <div className="relative flex h-28 w-28 items-center justify-center rounded-full bg-gradient-to-br from-primary via-primary/90 to-primary/80 shadow-2xl">
                      <Loader2 className="h-14 w-14 animate-spin text-white" />
                    </div>
                    <div className="absolute -inset-2 rounded-full bg-gradient-to-br from-primary/20 to-transparent blur-xl" />
                  </div>
                  <div className="space-y-4 text-center">
                    <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                      جاري معالجة البيانات
                    </h3>
                    <p className="max-w-md text-base leading-relaxed text-slate-600 dark:text-slate-400">
                      يتم الآن تحليل وتجهيز تقرير التوريد الخاص بك، يرجى
                      الانتظار قليلاً
                    </p>
                    <div className="mt-6 flex items-center justify-center gap-2">
                      <div className="h-3 w-3 animate-pulse rounded-full bg-primary" />
                      <div
                        className="h-3 w-3 animate-pulse rounded-full bg-primary"
                        style={{ animationDelay: '0.2s' }}
                      />
                      <div
                        className="h-3 w-3 animate-pulse rounded-full bg-primary"
                        style={{ animationDelay: '0.4s' }}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default CompanyItemsSupplyReportPage;
